/**
 * Builds and copies the diagnostics packet for support tickets.
 *
 * Schema per brief §15 "Diagnostics Packet":
 *   - incident_id          generated client-side; user pastes this into
 *                          the email so we can correlate.
 *   - generated_at         ISO timestamp.
 *   - email                signed-in email (or null if signed out).
 *   - app_version          from package.json via webpack DefinePlugin.
 *   - excel_platform       WIN32 / MAC / WEB / UNKNOWN.
 *   - office_version_bucket coarse band, not full build.
 *   - user_agent           raw navigator.userAgent for the WebView2 build.
 *   - recent_telemetry     last 50 safe events from the ring buffer.
 *   - recent_errors        last 20 masked error logs from the ring buffer.
 */

import { getAuthState } from "./auth";
import {
  APP_VERSION,
  getExcelPlatform,
  getOfficeVersionBucket,
} from "./clientContext";
import {
  getRecentErrors,
  getRecentTelemetry,
  type BufferedError,
  type BufferedTelemetry,
} from "./diagnosticsBuffer";

export interface DiagnosticsPacket {
  incident_id: string;
  generated_at: string;
  email: string | null;
  app_version: string;
  excel_platform: string;
  office_version_bucket: string;
  user_agent: string;
  recent_telemetry: BufferedTelemetry[];
  recent_errors: BufferedError[];
}

function generateIncidentId(): string {
  // `INC-` + 8 hex chars. Use crypto.randomUUID when available so two
  // simultaneous reports don't collide.
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return "INC-" + cryptoObj.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
  }
  return "INC-" + Math.random().toString(16).slice(2, 10).toUpperCase();
}

export function buildDiagnosticsPacket(): DiagnosticsPacket {
  const auth = getAuthState();
  const email = auth.kind === "signed_in" ? auth.email : null;
  return {
    incident_id: generateIncidentId(),
    generated_at: new Date().toISOString(),
    email,
    app_version: APP_VERSION,
    excel_platform: getExcelPlatform(),
    office_version_bucket: getOfficeVersionBucket(),
    user_agent: navigator.userAgent,
    recent_telemetry: getRecentTelemetry(),
    recent_errors: getRecentErrors(),
  };
}

/**
 * Build the packet, format as indented JSON, and copy to the user's
 * clipboard. Returns the byte length of the copied string for the UI
 * to display ("Copied N bytes…").
 */
export async function copyDiagnosticsToClipboard(): Promise<number> {
  const packet = buildDiagnosticsPacket();
  const json = JSON.stringify(packet, null, 2);
  // navigator.clipboard.writeText requires a secure context and a user
  // gesture — both apply here (HTTPS task pane + a click handler).
  await navigator.clipboard.writeText(json);
  return json.length;
}
